import { useRef, useState } from "react";
import {
    motion,
    useScroll,
    useMotionValueEvent,
    AnimatePresence,
} from "framer-motion";
import ConstructionScene from "@/three/ConstructionScene";

const STAGES = [
    { n: "01", name: "Foundation", desc: "Poured concrete, millimetre-true." },
    { n: "02", name: "Columns", desc: "Carved teak pillars rise in rhythm." },
    { n: "03", name: "Slab & Walls", desc: "Laterite and lime plaster take form." },
    { n: "04", name: "Glass", desc: "Light is invited in, on our terms." },
    { n: "05", name: "Roof", desc: "Timber slats crown the silhouette." },
    { n: "06", name: "Lighting", desc: "Warmth engineered into every lumen." },
    { n: "07", name: "Landscape", desc: "Water and green soften the geometry." },
    { n: "08", name: "The Villa", desc: "A Kerala courtyard, ready for life." },
];

export default function BuildingExperience() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });
    const [stage, setStage] = useState(0);

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        setStage(Math.min(STAGES.length - 1, Math.floor(v * STAGES.length)));
    });

    const current = STAGES[stage];

    return (
        <section
            id="experience"
            ref={ref}
            data-testid="building-experience-section"
            className="relative h-[440vh]"
        >
            <div className="sticky top-0 h-screen overflow-clip">
                <div className="pointer-events-none absolute inset-0">
                    <ConstructionScene progress={scrollYProgress} />
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-coal/90 to-transparent" />

                <div className="absolute left-6 top-28 sm:left-12 lg:left-20">
                    <p className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.45em] text-ash">
                        <span className="font-display text-xs tracking-[0.35em] text-gold">
                            03
                        </span>
                        <span className="h-px w-10 bg-gold/50" />
                        The Interactive Build
                    </p>
                </div>

                <div className="absolute bottom-16 left-6 right-6 sm:left-12 sm:right-auto sm:max-w-md lg:left-20">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={stage}
                            data-testid="build-stage-label"
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -18 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <p className="font-display text-xs font-medium tracking-[0.4em] text-gold">
                                {current.n} / 08
                            </p>
                            <h3 className="mt-3 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
                                {current.name}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-ash">
                                {current.desc}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="absolute right-6 top-1/2 hidden -translate-y-1/2 sm:right-12 sm:block lg:right-20">
                    <div className="relative h-56 w-px bg-white/10">
                        <motion.div
                            data-testid="build-progress-line"
                            className="absolute inset-x-0 top-0 h-full origin-top bg-gold"
                            style={{ scaleY: scrollYProgress }}
                        />
                    </div>
                    <p className="mt-4 text-right font-display text-xs tracking-[0.3em] text-ash">
                        {String(Math.round(scrollYProgress.get() * 100)).padStart(2, "0")}%
                    </p>
                </div>

                {stage === 0 && (
                    <motion.p
                        className="absolute bottom-16 right-6 hidden text-[10px] uppercase tracking-[0.4em] text-ash sm:right-12 md:block lg:right-20"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2.4, repeat: Infinity }}
                    >
                        Keep scrolling to construct
                    </motion.p>
                )}
            </div>
        </section>
    );
}
